import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { decodePolyline, directionsLinks, type GeoPlace, type Point, type RouteSummary } from '@rydres/shared';
import { inputClass } from './primitives.js';

/** The API's address search, the address at a point and drives; each app passes its own. */
export interface GeoApi {
  search: (q: string, near?: Point | null) => Promise<GeoPlace[]>;
  reverse: (p: Point) => Promise<GeoPlace | null>;
  route: (from: Point, to: Point) => Promise<RouteSummary>;
}

/** What an address field holds: the text, and its point when picked from a suggestion or the location. */
export interface PickedAddress {
  address: string;
  point: Point | null;
}

/** OpenFreeMap's free basemap, the one RydPal's maps use; it needs no key. */
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

/**
 * An address input that suggests places while typing: arrows and Enter pick one, Escape closes
 * the list. Typing clears the point; picking sets it. Without `geo` it is a plain input.
 */
export function AddressInput({
  id,
  value,
  onChange,
  geo,
  near,
  onFocus,
  placeholder,
  required,
  autoFocus,
}: {
  id?: string;
  value: string;
  onChange: (next: PickedAddress) => void;
  geo?: GeoApi | null;
  near?: Point | null;
  onFocus?: () => void;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
}) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<GeoPlace[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const asked = useRef(0);
  useEffect(() => {
    const q = query.trim();
    if (!geo || q.length < 3) return setItems([]);
    const n = ++asked.current;
    const t = setTimeout(() => {
      // Only the newest question's answer is shown; a slow older one is dropped.
      geo.search(q, near).then(
        (r) => {
          if (n !== asked.current) return;
          setItems(r);
          setActive(-1);
          setOpen(true);
        },
        () => n === asked.current && setItems([]),
      );
    }, 250);
    return () => clearTimeout(t);
  }, [query, geo, near?.lat, near?.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (p: GeoPlace) => {
    setQuery('');
    setItems([]);
    setOpen(false);
    onChange({ address: p.label, point: p.point });
  };
  const shown = open && items.length > 0;
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!items.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (e.key === 'ArrowDown' ? Math.min(items.length - 1, a + 1) : Math.max(-1, a - 1)));
    } else if (e.key === 'Enter' && shown && active >= 0) {
      e.preventDefault();
      pick(items[active]!);
    } else if (e.key === 'Escape' && shown) {
      e.preventDefault();
      setOpen(false);
    }
  };
  return (
    <div className="relative">
      <input
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown}
        aria-controls={listId}
        aria-activedescendant={shown && active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="street-address"
        maxLength={300}
        required={required}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange({ address: e.target.value, point: null });
        }}
        onFocus={() => {
          onFocus?.();
          if (items.length) setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={key}
        className={inputClass}
      />
      {shown && (
        <ul id={listId} role="listbox" className="absolute inset-x-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-2xl border border-line bg-card p-1 shadow-soft">
          {items.map((p, i) => (
            <li
              key={`${p.label}|${i}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              // Picks before the input loses focus, so the list does not vanish under the finger.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(p);
              }}
              className={`cursor-pointer rounded-xl px-3 py-2 ${i === active ? 'bg-card2' : 'hover:bg-card2'}`}
            >
              <div className="text-sm font-bold text-ink">{p.name}</div>
              {p.address !== p.name && <div className="text-xs text-muted">{p.address}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The browser's location as an address, for "Use my location"; asks permission the first time. */
export function useMyLocation(geo?: GeoApi | null) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = Boolean(geo) && typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const locate = useCallback(async (): Promise<PickedAddress | null> => {
    if (!geo || !supported) return null;
    setBusy(true);
    setError(null);
    try {
      const pos = await new Promise<GeolocationPosition>((ok, no) => navigator.geolocation.getCurrentPosition(ok, no, { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 }));
      const point = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      const place = await geo.reverse(point);
      if (!place) setError('No street address here. Type it instead.');
      // The exact spot, not the nearest address's: that is where the driver should come.
      return place ? { address: place.label, point } : null;
    } catch (err) {
      setError((err as GeolocationPositionError)?.code === 1 ? 'Location is off for this site. Allow it in the browser, or type the address.' : 'Could not find your location. Type the address instead.');
      return null;
    } finally {
      setBusy(false);
    }
  }, [geo, supported]);
  return { locate, busy, error, supported };
}

/** "About 48 mi · 47 min drive", saying so when it is only an estimate. */
export const driveText = (r: RouteSummary) => `About ${Math.round(r.miles)} mi · ${r.minutes < 60 ? `${r.minutes} min` : `${Math.floor(r.minutes / 60)} h ${r.minutes % 60} min`} drive${r.degraded ? ' (rough estimate)' : ''}`;

/** The drive between two points once both are known, or null. */
export function useRoute(geo: GeoApi | null | undefined, from: Point | null, to: Point | null) {
  const [route, setRoute] = useState<RouteSummary | null>(null);
  useEffect(() => {
    if (!geo || !from || !to) return setRoute(null);
    let live = true;
    geo.route(from, to).then((r) => live && setRoute(r), () => live && setRoute(null));
    return () => {
      live = false;
    };
  }, [geo, from?.lat, from?.lng, to?.lat, to?.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  return route;
}

/**
 * A small map of pickup (teal) and drop-off (blue) with the road between them, for checking the
 * places at a glance. MapLibre loads only when a map is shown. The map credit sits under the map,
 * always visible as OpenStreetMap asks, rather than over a third of it.
 */
export function MapPreview({ from, to, line, className = '' }: { from?: Point | null; to?: Point | null; line?: string | null; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const points = [from, to].filter((p): p is Point => Boolean(p));
    if (!box.current || !points.length) return;
    let map: { remove: () => void } | null = null;
    let cancelled = false;
    void (async () => {
      const [{ default: maplibregl }] = await Promise.all([import('maplibre-gl'), import('maplibre-gl/dist/maplibre-gl.css')]);
      if (cancelled || !box.current) return;
      const m = new maplibregl.Map({ container: box.current, style: MAP_STYLE, interactive: false, attributionControl: false });
      map = m;
      const path = line ? decodePolyline(line) : [];
      if (path.length)
        m.on('load', () => {
          m.addSource('route', { type: 'geojson', data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: path.map((p) => [p.lng, p.lat]) } } });
          m.addLayer({ id: 'route', type: 'line', source: 'route', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#2563eb', 'line-width': 4 } });
        });
      if (from) new maplibregl.Marker({ color: '#0d9488' }).setLngLat([from.lng, from.lat]).addTo(m);
      if (to) new maplibregl.Marker({ color: '#1e3a8a' }).setLngLat([to.lng, to.lat]).addTo(m);
      const all = [...points, ...path];
      if (all.length === 1) m.jumpTo({ center: [all[0]!.lng, all[0]!.lat], zoom: 14 });
      else {
        const bounds = new maplibregl.LngLatBounds();
        for (const p of all) bounds.extend([p.lng, p.lat]);
        m.fitBounds(bounds, { padding: 40, animate: false, maxZoom: 15 });
      }
    })();
    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [from?.lat, from?.lng, to?.lat, to?.lng, line]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!from && !to) return null;
  const credit = 'underline decoration-dotted';
  return (
    <div className={className}>
      <div ref={box} role="img" aria-label={from && to ? 'Map of the pickup and drop-off' : 'Map of the place'} className="h-48 w-full overflow-hidden rounded-2xl border border-line bg-card2" />
      <p className="mt-1 text-[11px] text-muted">
        Map <a className={credit} href="https://openfreemap.org" target="_blank" rel="noreferrer">OpenFreeMap</a> ©{' '}
        <a className={credit} href="https://www.openmaptiles.org/" target="_blank" rel="noreferrer">OpenMapTiles</a> · data ©{' '}
        <a className={credit} href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors
      </p>
    </div>
  );
}

/** Links that open turn-by-turn directions to a place in Google Maps, Apple Maps or Waze. */
export function DirectionsLinks({ label, address, point }: { label: string; address: string; point?: Point | null }) {
  const l = directionsLinks(address, point);
  const link = 'rounded-xl bg-card2 px-3 py-1.5 text-xs font-bold';
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="font-bold">{label}</span>
      <a className={link} href={l.google} target="_blank" rel="noreferrer">Google Maps</a>
      <a className={link} href={l.apple} target="_blank" rel="noreferrer">Apple Maps</a>
      <a className={link} href={l.waze} target="_blank" rel="noreferrer">Waze</a>
    </div>
  );
}
